import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

import { api } from "@/lib/api";

/**
 * Tiny status pill that pings GET /api/live-score/health and renders one of:
 *   - loading  → spinner ("Checking…")
 *   - ok       → green dot + "live-score-api · connected"
 *   - warn     → amber dot + the specific message from the backend (so the
 *                editor sees "keys not set" or "API rejected key/secret")
 *
 * Click → force re-check (bypasses the cache — useful after redeploying with
 * the env vars).
 *
 * Caching (2026-02, iteration 33):
 * The health probe hits live-score-api's /users/pair.json on every call. To
 * keep our upstream request budget under control we cache the last successful
 * (or "warn") result in sessionStorage for 10 minutes. Editors visiting the
 * Editor page repeatedly during a match-day therefore only fire the probe
 * a couple of times, not once per navigation. Manual click bypasses the cache.
 */

const CACHE_KEY = "dugsy.live-score-health";
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(entry) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ ...entry, at: Date.now() }),
    );
  } catch {
    /* sessionStorage may be unavailable — cache is best-effort */
  }
}

export default function LiveScoreStatusBadge() {
  const [state, setState] = useState({ status: "loading", message: "" });

  const check = async ({ force = false } = {}) => {
    if (!force) {
      const cached = readCache();
      if (cached) {
        setState({ status: cached.status, message: cached.message });
        return;
      }
    }
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.get("/live-score/health");
      const next = {
        status: data?.ok ? "ok" : "warn",
        message: data?.message || "",
      };
      setState(next);
      writeCache(next);
    } catch (err) {
      const next = {
        status: "warn",
        message:
          err?.response?.data?.detail ||
          err?.message ||
          "Could not check live-score-api status.",
      };
      setState(next);
      writeCache(next);
    }
  };

  useEffect(() => {
    check();
  }, []);

  const isOk = state.status === "ok";
  const isLoading = state.status === "loading";
  const dotColor = isLoading
    ? "bg-zinc-400 animate-pulse"
    : isOk
    ? "bg-emerald-500"
    : "bg-amber-500";
  const ring = isOk
    ? "border-emerald-200 bg-emerald-50/60 text-emerald-800"
    : isLoading
    ? "border-zinc-200 bg-zinc-50 text-zinc-600"
    : "border-amber-300 bg-amber-50/60 text-amber-900";
  const label = isLoading
    ? "Checking live-score-api…"
    : isOk
    ? "live-score-api · connected"
    : "live-score-api · needs attention";

  return (
    <button
      type="button"
      onClick={() => check({ force: true })}
      title={state.message || "Click to recheck (bypasses 10-min cache)"}
      data-testid="live-score-status-badge"
      data-status={state.status}
      className={`inline-flex items-center gap-2 h-7 px-2.5 rounded-full border text-[11px] font-semibold tracking-tight transition hover:brightness-95 ${ring}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} aria-hidden />
      {isLoading ? (
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
      ) : isOk ? (
        <CheckCircle2 className="h-3 w-3" aria-hidden />
      ) : (
        <AlertTriangle className="h-3 w-3" aria-hidden />
      )}
      <span data-testid="live-score-status-label">{label}</span>
    </button>
  );
}
